/**
 * Cohort Form Modal
 * Reusable modal for creating and editing cohorts
 */

import React, { useState, useEffect } from 'react';
import { X, Search, UserPlus, Trash2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const CohortFormModal = ({ isOpen, onClose, onSave, cohort = null, isLoading = false }) => {
  const [formData, setFormData] = useState({
    name: '',
    program_level: 'FRONTLINE_LEADERS',
    start_date: '',
    end_date: '',
    duration_months: 6,
    status: 'DRAFT'
  });

  const [errors, setErrors] = useState({});
  const [participants, setParticipants] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchDebounceTimer, setSearchDebounceTimer] = useState(null);
  const { getAccessToken } = useAuth();

  // Helper to format date from ISO to YYYY-MM-DD
  const formatDateForInput = (isoDate) => {
    if (!isoDate) return '';
    try {
      const date = new Date(isoDate);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };

  // Populate form when editing
  useEffect(() => {
    if (cohort) {
      setFormData({
        name: cohort.name || '',
        program_level: cohort.program_level || 'FRONTLINE_LEADERS',
        start_date: formatDateForInput(cohort.start_date),
        end_date: formatDateForInput(cohort.end_date),
        duration_months: cohort.duration_months || 6,
        status: cohort.status || 'DRAFT'
      });
      
      // Load existing participants if editing
      if (cohort.participants && Array.isArray(cohort.participants)) {
        const mappedParticipants = cohort.participants.map(p => ({
          employeeId: p.employee_id,
          mail: p.email,
          email: p.email,
          displayName: `${p.first_name} ${p.last_name}`,
          name: `${p.first_name} ${p.last_name}`,
          givenName: p.first_name,
          surname: p.last_name
        }));
        setParticipants(mappedParticipants);
      } else {
        setParticipants([]);
      }
    } else {
      // Reset form for new cohort
      setFormData({
        name: '',
        program_level: 'FRONTLINE_LEADERS',
        start_date: '',
        end_date: '',
        duration_months: 6,
        status: 'DRAFT'
      });
      setParticipants([]);
    }
    setErrors({});
    setSearchQuery('');
    setSearchResults([]);
  }, [cohort, isOpen]);

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Cohort name is required';
    }

    if (!formData.start_date) {
      newErrors.start_date = 'Start date is required';
    }

    if (!formData.end_date) {
      newErrors.end_date = 'End date is required';
    }

    if (formData.start_date && formData.end_date) {
      const start = new Date(formData.start_date);
      const end = new Date(formData.end_date);
      if (end <= start) {
        newErrors.end_date = 'End date must be after start date';
      }
    }

    if (!formData.duration_months || formData.duration_months < 1) {
      newErrors.duration_months = 'Duration must be at least 1 month';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      // Include participants in the save data
      onSave({
        ...formData,
        participants: participants.map(p => ({
          employee_id: p.employeeId,
          email: p.mail,
          first_name: p.givenName,
          last_name: p.surname
        }))
      });
    }
  };

  // Search EntraID users (debounced)
  const handleSearch = async (query) => {
    const searchTerm = query || searchQuery;
    
    if (!searchTerm.trim() || searchTerm.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setSearching(true);
    
    try {
      // Use the searchUsers function from cohortAPI instead of direct fetch
      const { searchUsers } = await import('../../services/cohortAPI');
      const response = await searchUsers(searchTerm);
      
      // Handle both {users: [...]} and {data: {users: [...]}} response formats
      const users = response.data?.users || response.users || [];
      setSearchResults(users);
    } catch (error) {
      console.error('Error searching users:', error);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  // Debounced search on input change
  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    // Clear existing timer
    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer);
    }
    
    // Set new timer for auto-search
    if (value.length >= 2) {
      const timer = setTimeout(() => {
        handleSearch(value);
      }, 500); // Wait 500ms after user stops typing
      setSearchDebounceTimer(timer);
    } else {
      setSearchResults([]);
    }
  };

  const handleAddParticipant = (user) => {
    // Check if already added
    if (participants.find(p => p.mail === user.mail)) {
      return;
    }
    
    setParticipants([...participants, user]);
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleRemoveParticipant = (email) => {
    setParticipants(participants.filter(p => p.mail !== email));
  };

  // Auto-calculate end date when start date or duration changes
  const calculateEndDate = (startDate, durationMonths) => {
    if (!startDate || !durationMonths) return '';
    
    try {
      const start = new Date(startDate);
      const end = new Date(start);
      end.setMonth(end.getMonth() + parseInt(durationMonths));
      
      // Format as YYYY-MM-DD
      const year = end.getFullYear();
      const month = String(end.getMonth() + 1).padStart(2, '0');
      const day = String(end.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error('Error calculating end date:', error);
      return '';
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    let updates = { [name]: value };
    
    // Auto-calculate end date when start date or duration changes
    if (name === 'start_date' || name === 'duration_months') {
      const startDate = name === 'start_date' ? value : formData.start_date;
      const duration = name === 'duration_months' ? value : formData.duration_months;
      const calculatedEndDate = calculateEndDate(startDate, duration);
      if (calculatedEndDate) {
        updates.end_date = calculatedEndDate;
      }
    }
    
    setFormData(prev => ({
      ...prev,
      ...updates
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '0.75rem',
        maxWidth: '600px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
      }}>
        {/* Header */}
        <div style={{
          padding: '1.5rem',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#111827', margin: 0 }}>
            {cohort ? 'Edit Cohort' : 'Create New Cohort'}
          </h2>
          <button
            onClick={onClose}
            style={{
              padding: '0.5rem',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              borderRadius: '0.375rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <X size={20} color="#6b7280" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ padding: '1.5rem' }}>
          {/* General Error Message */}
          {Object.keys(errors).length > 0 && (
            <div style={{
              padding: '1rem',
              background: '#fee2e2',
              border: '2px solid #dc2626',
              borderRadius: '0.5rem',
              marginBottom: '1.5rem'
            }}>
              <p style={{
                color: '#991b1b',
                fontSize: '0.875rem',
                fontWeight: '600',
                margin: 0,
                marginBottom: '0.5rem'
              }}>
                Please fix the following errors:
              </p>
              <ul style={{
                margin: 0,
                paddingLeft: '1.5rem',
                color: '#991b1b',
                fontSize: '0.875rem'
              }}>
                {Object.values(errors).map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}
          
          <div style={{ display: 'grid', gap: '1.5rem' }}>
            {/* Cohort Name */}
            <div>
              <label style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '0.5rem'
              }}>
                Cohort Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g., Q1 2026 Emerging Leaders"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: `1px solid ${errors.name ? '#ef4444' : '#d1d5db'}`,
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem'
                }}
              />
              {errors.name && (
                <p style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                  {errors.name}
                </p>
              )}
            </div>

            {/* Program Level */}
            <div>
              <label style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '0.5rem'
              }}>
                Program Level *
              </label>
              <select
                name="program_level"
                value={formData.program_level}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  background: 'white'
                }}
              >
                <option value="FRONTLINE_LEADERS">Frontline Leaders</option>
                <option value="INFORMAL_LEADERS">Informal Leaders</option>
                <option value="LEADERS_OF_LEADERS">Leaders of Leaders</option>
                <option value="EXPERT_LEADERS">Expert Leaders</option>
                <option value="VISIONARY_LEADERS">Visionary Leaders</option>
              </select>
            </div>

            {/* Date Range */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '0.5rem'
                }}>
                  Start Date *
                </label>
                <input
                  type="date"
                  name="start_date"
                  value={formData.start_date}
                  onChange={handleChange}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: `1px solid ${errors.start_date ? '#ef4444' : '#d1d5db'}`,
                    borderRadius: '0.5rem',
                    fontSize: '0.875rem'
                  }}
                />
                {errors.start_date && (
                  <p style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                    {errors.start_date}
                  </p>
                )}
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '0.5rem'
                }}>
                  End Date *
                </label>
                <input
                  type="date"
                  name="end_date"
                  value={formData.end_date}
                  onChange={handleChange}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: `1px solid ${errors.end_date ? '#ef4444' : '#d1d5db'}`,
                    borderRadius: '0.5rem',
                    fontSize: '0.875rem'
                  }}
                />
                {errors.end_date && (
                  <p style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                    {errors.end_date}
                  </p>
                )}
              </div>
            </div>

            {/* Duration */}
            <div>
              <label style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '0.5rem'
              }}>
                Duration (months) *
              </label>
              <input
                type="number"
                name="duration_months"
                value={formData.duration_months}
                onChange={handleChange}
                min="1"
                max="24"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: `1px solid ${errors.duration_months ? '#ef4444' : '#d1d5db'}`,
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem'
                }}
              />
              {errors.duration_months && (
                <p style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                  {errors.duration_months}
                </p>
              )}
            </div>

            {/* Status */}
            <div>
              <label style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '0.5rem'
              }}>
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  background: 'white'
                }}
              >
                <option value="DRAFT">Draft</option>
                <option value="ACTIVE">Active</option>
                <option value="COMPLETED">Completed</option>
                <option value="ARCHIVED">Archived</option>
              </select>
            </div>

            {/* Participants Section */}
            <div style={{
              marginTop: '1rem',
              padding: '1.5rem',
              background: '#f9fafb',
              borderRadius: '0.5rem',
              border: '1px solid #e5e7eb'
            }}>
              <h3 style={{
                fontSize: '1rem',
                fontWeight: '600',
                color: '#111827',
                marginBottom: '1rem'
              }}>
                Participants ({participants.length})
              </h3>

              {/* Search Box */}
              <div style={{ marginBottom: '1rem' }}>
                <label style={{
                    display: 'block',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Add Participants
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={handleSearchInputChange}
                      placeholder="Start typing name or email (min 2 characters)..."
                      style={{
                        width: '100%',
                        padding: '0.75rem 0.75rem 0.75rem 2.5rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '0.5rem',
                        fontSize: '0.875rem'
                      }}
                    />
                  <Search
                    size={18}
                    style={{
                      position: 'absolute',
                      left: '0.75rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: searching ? '#6366f1' : '#9ca3af'
                    }}
                  />
                  {searching && (
                    <div style={{
                      position: 'absolute',
                      right: '0.75rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      fontSize: '0.75rem',
                      color: '#6366f1'
                    }}>
                      Searching...
                    </div>
                  )}
                </div>
              </div>

              {/* Search Results */}
              {searchResults.length > 0 && (
                <div style={{
                  marginBottom: '1rem',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.5rem',
                  background: 'white'
                }}>
                  {searchResults.map((user) => (
                    <div
                      key={user.mail}
                      style={{
                        padding: '0.75rem',
                        borderBottom: '1px solid #e5e7eb',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: '600', fontSize: '0.875rem', color: '#111827' }}>
                          {user.displayName}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                          {user.mail}
                        </div>
                        {user.jobTitle && (
                          <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                            {user.jobTitle}
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleAddParticipant(user)}
                        disabled={participants.find(p => p.mail === user.mail)}
                        style={{
                          padding: '0.5rem',
                          background: participants.find(p => p.mail === user.mail) ? '#e5e7eb' : '#10b981',
                          color: participants.find(p => p.mail === user.mail) ? '#6b7280' : 'white',
                          border: 'none',
                          borderRadius: '0.375rem',
                          cursor: participants.find(p => p.mail === user.mail) ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                          fontSize: '0.75rem',
                          fontWeight: '600'
                        }}
                      >
                        <UserPlus size={14} />
                        {participants.find(p => p.mail === user.mail) ? 'Added' : 'Add'}
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Enrolled Participants List */}
              {participants.length > 0 ? (
                <div style={{
                  maxHeight: '200px',
                  overflowY: 'auto',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.5rem',
                  background: 'white'
                }}>
                  {participants.map((participant) => (
                    <div
                      key={participant.mail}
                      style={{
                        padding: '0.75rem',
                        borderBottom: '1px solid #e5e7eb',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: '600', fontSize: '0.875rem', color: '#111827' }}>
                          {participant.displayName}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                          {participant.mail}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveParticipant(participant.mail)}
                        style={{
                          padding: '0.5rem',
                          background: '#fee2e2',
                          color: '#dc2626',
                          border: 'none',
                          borderRadius: '0.375rem',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{
                  padding: '2rem',
                  textAlign: 'center',
                  color: '#9ca3af',
                  fontSize: '0.875rem',
                  border: '1px dashed #d1d5db',
                  borderRadius: '0.5rem',
                  background: 'white'
                }}>
                  No participants added yet. Search and add participants above.
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div style={{
            marginTop: '2rem',
            display: 'flex',
            gap: '1rem',
            justifyContent: 'flex-end'
          }}>
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              style={{
                padding: '0.75rem 1.5rem',
                background: '#f3f4f6',
                border: '1px solid #d1d5db',
                borderRadius: '0.5rem',
                fontSize: '0.875rem',
                fontWeight: '600',
                color: '#374151',
                cursor: isLoading ? 'not-allowed' : 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              style={{
                padding: '0.75rem 1.5rem',
                background: isLoading ? '#9ca3af' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                border: 'none',
                borderRadius: '0.5rem',
                fontSize: '0.875rem',
                fontWeight: '600',
                color: 'white',
                cursor: isLoading ? 'not-allowed' : 'pointer'
              }}
            >
              {isLoading ? 'Saving...' : (cohort ? 'Update Cohort' : 'Create Cohort')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CohortFormModal;
